import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {LEGACY_FONT_PROXIMA_13} from 'styles/FontConstants';
import {lrSplit} from 'styles/Utilities';
import ModalButtonKey from 'test/lib/cypress/shared/ModalButtonKey';
import {TooltipWrapper} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {BLUE_LIGHT, BLUE_LIGHTEST, GRAY_DARK, GRAY_LIGHT} from 'ui-library/Colors';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import MenuPopover from 'ui-library/menus/MenuPopover';

interface AddParamProps {
  text: string;
  disabledReason?: string | null;
  onAddParam: () => void;
}

function AddParam(props: AddParamProps): JSX.Element {
  return (
    <TooltipWrapper tooltipTitle={props.disabledReason}>
      <span
        className={classNames(
          css(styles.addParam, Boolean(props.disabledReason) && styles.addParamDisabled),
          '__addParam'
        )}
        onClick={props.disabledReason ? () => {} : props.onAddParam}
      >
        <BenchlingIcon className={css(styles.addIcon)} icon="search-filter-add" />
        <span>{props.text}</span>
      </span>
    </TooltipWrapper>
  );
}

export interface SearchParamDropdownProps {
  title: string;
  icon: BenchlingIconName;
  addText: string;
  addDisabledReason?: string | null;
  buttonSize: ButtonSize;
  buttonStyle: ButtonStyle;
  buttonDisabled?: boolean;
  addButtonHidden?: boolean;
  children: JSX.Element;
  onAddParam: () => void;
  isOpen: boolean;
  onToggle: (isOpen: boolean) => void;
  dropLeft?: boolean;
}

export default function SearchParamDropdown(props: SearchParamDropdownProps): JSX.Element {
  return (
    <Dropdown
      isOpen={props.isOpen}
      onToggle={props.onToggle}
      closeOnInsideClick={false}
      closeOnOutsideClick={true}
      menu={
        <MenuPopover className={css(styles.menu)} width={MenuWidth.FIT_CONTENT} dropLeft={props.dropLeft}>
          <div>{props.children}</div>
          <div className={css(styles.footer)}>
            {!props.addButtonHidden ? (
              <AddParam
                text={props.addText}
                disabledReason={props.addDisabledReason}
                onAddParam={props.onAddParam}
              />
            ) : null}
            <Button
              size={props.buttonSize}
              buttonStyle={ButtonStyle.SECONDARY}
              text="Apply"
              onClick={() => {
                props.onToggle(false);
              }}
              dataTestAttrs={{component: 'SearchParamDropdown', key: ModalButtonKey.APPLY}}
            />
          </div>
        </MenuPopover>
      }
    >
      <Button
        buttonStyle={props.buttonStyle}
        icon={props.icon}
        size={props.buttonSize}
        text={props.title}
        disabled={props.buttonDisabled}
        dataTestAttrs={{component: 'SearchParamDropdown', key: 'toggle'}}
      />
    </Dropdown>
  );
}

const styles = StyleSheet.create({
  menu: {
    ...LEGACY_FONT_PROXIMA_13,
  },
  footer: {
    borderTopColor: GRAY_LIGHT,
    borderTopStyle: 'solid',
    borderTopWidth: 1,
    padding: 10,
    ...lrSplit,
  },
  addParamDisabled: {
    color: BLUE_LIGHTEST,
    cursor: 'default',
  },
  addParam: {
    color: BLUE_LIGHT,
    cursor: 'pointer',
    // Disable selection so users can rapidly add params without selecting text via double-click
    userSelect: 'none',
  },
  addIcon: {
    height: 12,
    marginRight: 10,
    width: 12,
  },
  text: {
    color: GRAY_DARK,
    padding: '14px 12px',
  },
});
